/**
 * Resume соло-попытки открытого квиза (Фаза 10, И11; план §C6.2): персистенция
 * {slug, attemptId, token, deck} в `sessionStorage`, чтобы F5 не терял прогресс.
 *
 * Отличия от activeSession.ts (live-сессии):
 *  - сервер НЕ пере-выдаёт вопросы на resume (`POST …/state` отдаёт только
 *    отвеченные questionId) — поэтому здесь храним и весь deck из ответа старта;
 *  - TTL = 24 ч — синхронен серверному sweep-окну `openAttemptSweepAge`
 *    (active → abandoned; §C4.2): запись старше не восстанавливаем;
 *  - токен попытки — capability на запись ЭТОЙ попытки (X-Attempt-Token);
    запись привязана к slug: ссылку могли отрыть в другой вкладке квиза.
 */

import type { OpenQuestion } from './api';

const KEY = 'kikayoo.openAttempt';
const TTL_MS = 24 * 60 * 60 * 1000;

/** Запись активной попытки (в sessionStorage добавляется ts-штамп). */
export interface OpenAttemptRecord {
  slug: string;
  attemptId: number;
  token: string;
  totalQuestions: number;
  /** Deck вопросов из ответа старта (kindData без правильных ответов). */
  questions: OpenQuestion[];
}

interface Stamped {
  ts: number;
}

function readStamped(): (OpenAttemptRecord & Stamped) | null {
  if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as OpenAttemptRecord & Stamped;
    if (typeof parsed?.ts !== 'number') return null;
    if (Date.now() - parsed.ts > TTL_MS) {
      // Серверный sweep уже перевёл попытку в abandoned — не восстанавливаем.
      window.sessionStorage.removeItem(KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** Сохранить активную попытку (вызывается при успешном старте/новой попытке). */
export function saveOpenAttempt(record: OpenAttemptRecord): void {
  if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') return;
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify({ ...record, ts: Date.now() }));
  } catch {
    // private mode / quota — resume просто не сработает (попытка живёт на сервере).
  }
}

/** Активная попытка конкретного slug, если валидна (моложе TTL); иначе null. */
export function loadOpenAttempt(slug: string): OpenAttemptRecord | null {
  const v = readStamped();
  if (!v || v.slug !== slug) return null;
  if (
    typeof v.attemptId !== 'number' ||
    typeof v.token !== 'string' ||
    !Array.isArray(v.questions)
  ) {
    return null;
  }
  return {
    slug: v.slug,
    attemptId: v.attemptId,
    token: v.token,
    totalQuestions: v.totalQuestions,
    questions: v.questions,
  };
}

/** Стереть запись (финиш/выход/потеря попытки — 401/404). */
export function clearOpenAttempt(): void {
  if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') return;
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}
